import React, { useState, useEffect } from 'react';
import { useProfile } from '../hooks/useProfile';
import { QRCodeExport } from '../components/QRCodeExport';
import {
  Save,
  Trash2,
  Building,
  Mail,
  Phone,
  Globe,
  User,
  Copy,
  ExternalLink,
  Linkedin,
  Github,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  MessageCircle,
  Pencil,
  MessageSquare,
  Video,
  BookOpen,
  Send,
  Share2,
  Palette,
  MapPin,
  Briefcase,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';
import { auth } from '../lib/firebase';
import { logger } from '../lib/logger';

interface SubscriptionInfo {
  status: 'trial' | 'active' | 'expired';
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  cancelAt?: number;
  trialEndDate?: number;
  vcardEnabled: boolean;
}

const TabButton = ({ isActive, icon, label, onClick, className }: { isActive: boolean; icon: React.ReactNode; label: string; onClick: () => void; className?: string }) => {
  return (
    <button
      onClick={onClick}
      className={`relative flex flex-col items-center justify-center p-4 group ${
        isActive ? 'text-primary-600' : 'text-gray-500 hover:text-primary-600'
      } ${className}`}
    >
      <div className="relative">
        {icon}
        <span className="hidden md:block absolute left-1/2 transform -translate-x-1/2 -bottom-6 px-2 py-1 text-sm whitespace-nowrap
          bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {label}
        </span>
      </div>
    </button>
  );
};

const tabs = [
  { id: 'profil', label: 'Profil', icon: <User className="w-6 h-6" /> },
  { id: 'reseaux', label: 'Réseaux Sociaux', icon: <Globe className="w-6 h-6" /> },
  { id: 'personnalisation', label: 'Personnalisation', icon: <Settings className="w-6 h-6" /> },
  { id: 'partage', label: 'Partage', icon: <Share2 className="w-6 h-6" /> },
  { id: 'abonnement', label: 'Abonnement', icon: <Building className="w-6 h-6" /> },
];

export default function Profile() {
  const { profile, setProfile, loading, saveProfile, uploadLogo, uploadWallpaperBlob, uploadQRCode, deleteAccount } = useProfile();
  const [activeTab, setActiveTab] = useState('profil');
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [initialUsername] = useState(profile.username || '');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [urlInput, setUrlInput] = useState(profile.username || '');
  const [password, setPassword] = useState('');
  const [needsReauth, setNeedsReauth] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setUrlInput(profile.username || '');
  }, [profile.username]);

  const publicUrl = profile.username ? `${window.location.origin}/p/${profile.username}` : '';

  const copyPublicLink = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      toast.success('Lien copié !');
    }
  };

  const handleSave = async () => {
    // Si nous sommes dans l'onglet partage et que l'URL a été modifiée
    if (activeTab === 'partage' && profile.username !== initialUsername && profile.qrCodeUrl) {
      setShowConfirmModal(true);
      return;
    }

    await saveProfileChanges();
  };

  const handleCancelUrlChange = () => {
    setUrlInput(initialUsername);
    setProfile(prevProfile => ({
      ...prevProfile,
      username: initialUsername
    }));
    setShowConfirmModal(false);
  };

  const handleUsernameChange = (value: string) => {
    const cleanUsername = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    setUrlInput(cleanUsername);
    setProfile(prevProfile => ({
      ...prevProfile,
      username: cleanUsername
    }));
  };

  const saveProfileChanges = async () => {
    setSaving(true);
    try {
      await saveProfile(profile);
      toast.success('Profil mis à jour');
      setShowConfirmModal(false);
    } catch (error) {
      const errorMessage = logger.error('database', 'Erreur de sauvegarde du profil', error);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount(password);
      setShowDeleteModal(false);
      navigate('/login');
    } catch (error: any) {
      const errorMessage = logger.error('database', 'Erreur lors de la suppression du profil', error);
      toast.error(errorMessage);
      if (error.code === 'auth/requires-recent-login') {
        // Déconnecter l'utilisateur et le rediriger vers la page de connexion
        await auth.signOut();
        navigate('/login', { 
          state: { 
            message: 'Pour des raisons de sécurité, veuillez vous reconnecter avant de supprimer votre compte.',
            returnTo: '/profile'
          } 
        });
      }
    } finally {
      setIsDeleting(false);
      setPassword(''); // Réinitialiser le mot de passe
    }
  };

  const handleCancelSubscription = async () => {
    if (!profile.id) return;
    
    setCancellingSubscription(true);
    try {
      const response = await fetch('/.netlify/functions/stripe-cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: profile.id }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      toast.success('Votre abonnement sera annulé à la fin de la période en cours');
      setShowCancelModal(false);
    } catch (error) {
      const errorMessage = logger.error('network', 'Erreur lors de l\'annulation de l\'abonnement', error);
      toast.error(errorMessage);
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
      <div className="bg-white shadow rounded-lg">
        {/* Navigation par onglets */}
        <div className="border-b border-gray-200">
          <nav className="flex justify-center space-x-4 md:space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                isActive={activeTab === tab.id}
                icon={tab.icon}
                label={tab.label}
                onClick={() => setActiveTab(tab.id)}
                className={`${tab.id === 'reseaux' ? 'social-links-tab' : ''} 
                          ${tab.id === 'personnalisation' ? 'personnalisation-tab' : ''}
                          ${tab.id === 'partage' ? 'partage-tab' : ''}`}
              />
            ))}
          </nav>
        </div>

        {/* Contenu des onglets */}
        <div className="p-2 sm:p-6">
          {activeTab === 'profil' && (
            <div className="space-y-8 profile-info-section">
              {/* Informations personnelles */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Informations personnelles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="block text-sm text-gray-700">Prénom</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={profile.prenom}
                        onChange={(e) => setProfile({ ...profile, prenom: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="Votre prénom"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Nom</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={profile.nom}
                        onChange={(e) => setProfile({ ...profile, nom: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Entreprise</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Briefcase className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={profile.entreprise}
                        onChange={(e) => setProfile({ ...profile, entreprise: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="Nom de votre entreprise"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Poste</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Briefcase className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={profile.poste}
                        onChange={(e) => setProfile({ ...profile, poste: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="Votre poste"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Site Web</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Globe className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.siteWeb}
                        onChange={(e) => setProfile({ ...profile, siteWeb: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://www.votresite.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Informations de contact */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Informations de contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="block text-sm text-gray-700">Email</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="vous@exemple.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Téléphone</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        value={profile.telephone}
                        onChange={(e) => setProfile({ ...profile, telephone: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                      <div>
                        <label className="block text-sm text-gray-700">Adresse</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={profile.adresse}
                            onChange={(e) => setProfile({ ...profile, adresse: e.target.value })}
                            className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                            placeholder="Votre adresse"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-700">Ville</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={profile.ville}
                            onChange={(e) => setProfile({ ...profile, ville: e.target.value })}
                            className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                            placeholder="Votre ville"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                      <div>
                        <label className="block text-sm text-gray-700">Code Postal</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={profile.codePostal}
                            onChange={(e) => setProfile({ ...profile, codePostal: e.target.value })}
                            className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                            placeholder="Code postal"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-700">Pays</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Globe className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={profile.pays}
                            onChange={(e) => setProfile({ ...profile, pays: e.target.value })}
                            className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                            placeholder="Pays"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder
                      </>
                    )}
                  </button>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-red-600 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer mon compte
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reseaux' && (
            <div className="space-y-8">
              {/* Réseaux professionnels */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Réseaux professionnels</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="block text-sm text-gray-700">LinkedIn</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Linkedin className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.linkedin}
                        onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://linkedin.com/in/username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">GitHub</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Github className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.github}
                        onChange={(e) => setProfile({ ...profile, github: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://github.com/username"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Réseaux sociaux */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Réseaux sociaux</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="block text-sm text-gray-700">Twitter / X</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Twitter className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.x}
                        onChange={(e) => setProfile({ ...profile, x: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://twitter.com/username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Facebook</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Facebook className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.facebook}
                        onChange={(e) => setProfile({ ...profile, facebook: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://facebook.com/username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Instagram</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Instagram className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.instagram}
                        onChange={(e) => setProfile({ ...profile, instagram: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://instagram.com/username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">YouTube</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Youtube className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.youtube}
                        onChange={(e) => setProfile({ ...profile, youtube: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://youtube.com/@username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">TikTok</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Video className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.tiktok}
                        onChange={(e) => setProfile({ ...profile, tiktok: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://tiktok.com/@username"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Messagerie */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Messagerie</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="block text-sm text-gray-700">Discord</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MessageCircle className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={profile.discord}
                        onChange={(e) => setProfile({ ...profile, discord: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="username#0000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">WhatsApp</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MessageSquare className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        value={profile.whatsapp}
                        onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Telegram</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Send className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={profile.telegram}
                        onChange={(e) => setProfile({ ...profile, telegram: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="@username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Skype</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MessageCircle className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={profile.skype}
                        onChange={(e) => setProfile({ ...profile, skype: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="live:username"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Réseaux créatifs */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Réseaux créatifs</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="block text-sm text-gray-700">Behance</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Pencil className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.behance}
                        onChange={(e) => setProfile({ ...profile, behance: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://behance.net/username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Dribbble</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Pencil className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.dribbble}
                        onChange={(e) => setProfile({ ...profile, dribbble: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://dribbble.com/username"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Medium</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <BookOpen className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={profile.medium}
                        onChange={(e) => setProfile({ ...profile, medium: e.target.value })}
                        className="block w-full pl-10 rounded-md border-gray-300 focus:border-primary-600 focus:ring-primary-600 text-sm"
                        placeholder="https://medium.com/@username"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'abonnement' && (
            <div className="space-y-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">État de l'abonnement</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Statut</p>
                    <p className="mt-1 font-medium">
                      {profile.subscription?.status === 'active' && 'Actif'}
                      {profile.subscription?.status === 'trial' && 'Période d\'essai'}
                      {profile.subscription?.status === 'expired' && 'Expiré'}
                    </p>
                  </div>
                  
                  {profile.subscription?.status === 'trial' && (
                    <div>
                      <p className="text-sm text-gray-500">Expire le</p>
                      <p className="mt-1 text-orange-600">
                        {new Date(profile.subscription.trialEndDate || 0).toLocaleDateString()}
                      </p>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => navigate('/subscribe')}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          S'abonner maintenant
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {profile.subscription?.status === 'active' && (
                    <>
                      {profile.subscription.cancelAt ? (
                        <div>
                          <p className="text-sm text-gray-500">Fin de l'abonnement</p>
                          <p className="mt-1 text-orange-600">
                            Se termine le {new Date(profile.subscription.cancelAt * 1000).toLocaleDateString()}
                          </p>
                        </div>
                      ) : (
                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={() => setShowCancelModal(true)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Annuler l'abonnement
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {profile.subscription?.status === 'expired' && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => navigate('/subscribe')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Réactiver l'abonnement
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'partage' && (
            <div className="space-y-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Partage de votre VCard</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Partagez votre VCard avec vos contacts en utilisant le lien ci-dessous.
                </p>
                <div className="space-y-4">
                  <div className="sm:col-span-4">
                    <label htmlFor="public-url" className="block text-sm font-medium text-gray-700">
                      Votre lien public
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                        {window.location.origin}/p/
                      </span>
                      <input
                        type="text"
                        name="username"
                        id="username"
                        value={urlInput}
                        onChange={(e) => {
                          setUrlInput(e.target.value);
                          const updatedProfile = { ...profile, username: e.target.value };
                          setProfile(updatedProfile);
                          saveProfile(updatedProfile);
                        }}
                        className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={copyPublicLink}
                        className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copier
                      </button>
                      <a
                        href={`/p/${urlInput}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Voir
                      </a>
                    </div>
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Sauvegarder
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'personnalisation' && (
            <div className="space-y-6">
              <div className="qr-generator">
                <h3 className="text-lg font-medium text-gray-900">QR Code</h3>
                <QRCodeExport
                  value={profile.vcardUrl || ''}
                  name={`${profile.prenom}_${profile.nom}`}
                  logoUrl={profile.logoUrl}
                  wallpaperUrl={profile.wallpaperUrl}
                  onLogoUpload={async (file) => {
                    const logoUrl = await uploadLogo(file);
                    // Mettre à jour le profil avec le nouveau logo
                    const updatedProfile = {
                      ...profile,
                      logoUrl
                    };
                    await saveProfile(updatedProfile);
                  }}
                  onWallpaperSave={uploadWallpaperBlob}
                  onQRCodeSave={async (blob) => {
                    const qrCodeUrl = await uploadQRCode(blob);
                    // Mettre à jour le profil avec le nouveau QR code
                    const updatedProfile = {
                      ...profile,
                      qrCodeUrl
                    };
                    await saveProfile(updatedProfile);
                    return qrCodeUrl;
                  }}
                  qrStyle={profile.qrStyle}
                  onStyleChange={(style) => setProfile({ ...profile, qrStyle: style })}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">Supprimer le compte</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Attention : cette action est irréversible. Toutes vos données seront définitivement supprimées.
                </p>
                <div className="mt-4">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Confirmez votre mot de passe
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                    placeholder="Votre mot de passe"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPassword('');
                }}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={!password || isDeleting}
                className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white
                  ${isDeleting || !password
                    ? 'bg-red-300 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'}`}
              >
                {isDeleting ? 'Suppression...' : 'Supprimer mon compte'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation pour la modification de l'URL */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start space-x-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">Confirmer le changement d'URL</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Attention : la modification de l'URL va générer un nouveau QR code. 
                  Les anciens QR codes que vous avez déjà distribués ne fonctionneront plus. 
                  Voulez-vous continuer ?
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelUrlChange}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={saveProfileChanges}
                className="inline-flex justify-center rounded-md border border-transparent bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
              >
                Confirmer le changement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation d'annulation */}
      {showCancelModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Confirmer l'annulation
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Êtes-vous sûr de vouloir annuler votre abonnement ? Vous aurez toujours accès aux fonctionnalités jusqu'à la fin de la période en cours.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={cancellingSubscription}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                >
                  {cancellingSubscription ? 'Annulation...' : 'Confirmer l\'annulation'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancellingSubscription}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}